import translations from "./ui-copy.json";

const STORAGE_KEY = "zdeutsch.ui-language.v1";
const LANGUAGE_CODES = { Arabic: "ar", English: "en", French: "fr", German: "de", Bengali: "bn", Chinese: "zh", Czech: "cs", Danish: "da", Dutch: "nl", Finnish: "fi", Greek: "el", Hebrew: "he", Hindi: "hi", Hungarian: "hu", Indonesian: "id", Italian: "it", Japanese: "ja", Korean: "ko", Norwegian: "no", Polish: "pl", Portuguese: "pt", Romanian: "ro", Russian: "ru", Spanish: "es", Swedish: "sv", Thai: "th", Turkish: "tr", Ukrainian: "uk", Urdu: "ur", Vietnamese: "vi" };
export function resolveUiLanguage(value, fallback = "en") {
  const name = String(value || "").trim().toLowerCase();
  if (/^(ar(?:[-_].*)?|arabic|العربية)$/.test(name)) return "ar";
  if (/^(fr(?:[-_].*)?|french|français|francais)$/.test(name)) return "fr";
  if (/^(de(?:[-_].*)?|german|deutsch)$/.test(name)) return "de";
  if (/^(en(?:[-_].*)?|english)$/.test(name)) return "en";
  return fallback;
}
export function currentUiLanguage() {
  if (typeof window === "undefined") return "en";
  if (window.ZDeutschUiLanguage) return window.ZDeutschUiLanguage;
  try {
    const user = JSON.parse(sessionStorage.getItem("zdeutsch.session.snapshot.v2") || "null")?.payload?.user;
    if (user?.nativeLanguage) return resolveUiLanguage(user.nativeLanguage);
    return resolveUiLanguage(new URLSearchParams(location.search).get("lang") || localStorage.getItem(STORAGE_KEY) || document.documentElement.lang);
  } catch { return "en"; }
}
export function setUiLanguage(value) {
  const language = resolveUiLanguage(value);
  if (typeof window !== "undefined") {
    window.ZDeutschUiLanguage = language;
    document.documentElement.lang = language;
    try { localStorage.setItem(STORAGE_KEY, language); } catch {}
  }
  return language;
}
export function dateLocale(language = currentUiLanguage()) {
  return { ar: "ar-MA", fr: "fr-FR", de: "de-DE", en: "en-GB" }[resolveUiLanguage(language)];
}
export function languageLabel(value, language = currentUiLanguage()) {
  try { return new Intl.DisplayNames([language], { type: "language" }).of(LANGUAGE_CODES[value] || resolveUiLanguage(value)) || value; } catch { return value; }
}
const patterns = Object.entries(translations).filter(([key]) => /\{\d+\}/.test(key)).map(([key, copy]) => {
  const keys = [...key.matchAll(/\{(\d+)\}/g)].map(match => match[1]);
  const pattern = key.split(/\{\d+\}/).map(part => part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("([\\s\\S]+?)");
  return { pattern: new RegExp(`^${pattern}$`), keys, copy };
});
// Only interface strings are passed here. German exercise content is never translated.
export function uiText(language, value, arabic) {
  language = resolveUiLanguage(language);
  if (typeof value === "function") return (...args) => uiText(language, value(...args), typeof arabic === "function" ? arabic(...args) : undefined);
  if (typeof value !== "string") {
    if (Array.isArray(value)) return value.map((item, index) => uiText(language, item, arabic?.[index]));
    if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, uiText(language, item, arabic?.[key])]));
    return value;
  }
  if (language === "ar" && arabic !== undefined) return arabic;
  const translated = translations[value]?.[language];
  if (translated !== undefined) return translated;
  for (const item of patterns) {
    const match = item.pattern.exec(value);
    if (match && item.copy[language]) return item.copy[language].replace(/\{(\d+)\}/g, (_, index) => match[item.keys.indexOf(index) + 1] || "");
  }
  return value;
}
export function withFrench(tables) {
  return { ...tables, fr: tables.fr || uiText("fr", tables.en), de: tables.de || tables.en };
}
export function t(value) { return uiText(currentUiLanguage(), value); }

if (typeof window !== "undefined") window.ZDeutschUi = { resolveUiLanguage, currentUiLanguage, setUiLanguage, dateLocale, languageLabel, uiText, withFrench, t };

// Used only for static interface markup and assistant dialogs, before learner text is inserted.
export function localizeMarkup(markup) {
  const template = document.createElement("template");
  template.innerHTML = markup;
  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    const key = node.textContent.trim();
    if (translations[key] && !node.parentElement?.closest("script, style")) node.textContent = node.textContent.replace(key, t(key));
  }
  template.content.querySelectorAll("[aria-label], [title], [placeholder]").forEach(element => {
    ["aria-label", "title", "placeholder"].forEach(attribute => {
      if (element.hasAttribute(attribute)) element.setAttribute(attribute, t(element.getAttribute(attribute)));
    });
  });
  return template.innerHTML;
}
if (typeof window !== "undefined") window.ZDeutschUi.localizeMarkup = localizeMarkup;

export function uiError(value, fallback = "We could not complete this request. Please try again.") {
  const language = currentUiLanguage(), message = String(value || fallback);
  if (language === "en") return /[\u0600-\u06ff]/.test(message) ? t(fallback) : uiText(language, message);
  return translations[message]?.[language] || translations[fallback]?.[language] || translations["We could not complete this request. Please try again."]?.[language] || fallback;
}
if (typeof window !== "undefined") window.ZDeutschUi.uiError = uiError;
